import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { type UVInsertConfig, isUvInsertUserConfigurableText } from '@shared/models/uv-inserts';
import type { ResolvedUvInserts } from '@shared/view/uv-inserts/resolve-uv-inserts';
import { resolveUvInserts } from '@shared/view/uv-inserts/resolve-uv-inserts';
import {
  buildUvInsertCartLineItem,
  type UvInsertCartLineItem,
} from '@shared/view/uv-inserts/build-cart-line-item';
import { logError } from '../../utilities/logging.js';
import { readGiftWrapUvInsertsBootstrapPayload } from '../bootstrap';

const emptyResolvedUvInserts: ResolvedUvInserts = {
  configs: [],
  templateIds: {},
  suggestedMessaging: [],
};

export type ImageTransform = {
  scale: number;
  x: number;
  y: number;
};

type UvInsertsContextType = {
  /** Populated after mount via bootstrap + resolveUvInserts. */
  resolved: ResolvedUvInserts | null;
  /** Clears flow-only state when the gift wrap modal closes. */
  resetUvInsertFlowState: () => void;
  /** Active config for the current visualiser page — set by UvInsertVisualiserPage on mount. */
  activeConfig: UVInsertConfig | null;
  setActiveConfig: (config: UVInsertConfig | null) => void;
  /** Active face tab index within the visualiser. */
  activeTabIndex: number;
  setActiveTabIndex: (index: number) => void;
  /** Theme (variant) index for themed inserts / predesigned cards. */
  themeIndex: number;
  setThemeIndex: (index: number) => void;
  /** Text keyed by lineItemPropertyKey (e.g. UVFrontText). */
  textByKey: Record<string, string>;
  setText: (key: string, value: string) => void;
  /** Image file keyed by lineItemPropertyKey (for display only — cart uses UserInputContext). */
  imageByKey: Record<string, File | null>;
  setImageByKey: React.Dispatch<React.SetStateAction<Record<string, File | null>>>;
  /** Pinch/pan transform keyed by lineItemPropertyKey. */
  imageTransformByKey: Record<string, ImageTransform>;
  setImageTransformByKey: React.Dispatch<React.SetStateAction<Record<string, ImageTransform>>>;
  /** Theme chooser panel open state. */
  themeChooserOpen: boolean;
  setThemeChooserOpen: (open: boolean) => void;
  /** Suggestion chooser panel open state. */
  suggestionChooserOpen: boolean;
  setSuggestionChooserOpen: (open: boolean) => void;
  /** Selected suggestion group index. */
  selectedSuggestionGroupIndex: number;
  setSelectedSuggestionGroupIndex: (index: number) => void;
  /**
   * Builds a cart line item from MVVM data for the currently active config.
   * Returns null when no config is active or no valid theme variant exists.
   * giftWrapKey and parentTitle are generated at add-to-cart time by the caller.
   */
  buildCartLineItem: (options: {
    giftWrapKey: string;
    parentTitle: string;
    vendor?: string | null;
  }) => UvInsertCartLineItem | null;
};

const UvInsertsContext = createContext<UvInsertsContextType | undefined>(undefined);

type UvInsertsContextProviderProps = {
  children: React.ReactNode;
};

/**
 * Resolves UV inserts from Liquid bootstrap once on mount and holds flow state for the inserts UI.
 * Wraps GiftWrapProvider at the app root so GiftWrapContext can read `resolved` (e.g. cart skip rules).
 */
export function UvInsertsContextProvider({ children }: UvInsertsContextProviderProps) {
  const [resolved, setResolved] = useState<ResolvedUvInserts | null>(null);
  const [activeConfig, setActiveConfig] = useState<UVInsertConfig | null>(null);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [themeIndex, setThemeIndex] = useState(0);
  const [textByKey, setTextByKey] = useState<Record<string, string>>({});
  const [imageByKey, setImageByKey] = useState<Record<string, File | null>>({});
  const [imageTransformByKey, setImageTransformByKey] = useState<Record<string, ImageTransform>>({});
  const [themeChooserOpen, setThemeChooserOpen] = useState(false);
  const [suggestionChooserOpen, setSuggestionChooserOpen] = useState(false);
  const [selectedSuggestionGroupIndex, setSelectedSuggestionGroupIndex] = useState(0);

  // Resolve UV inserts config once on mount.
  useEffect(() => {
    try {
      setResolved(resolveUvInserts({ payload: readGiftWrapUvInsertsBootstrapPayload() }));
    } catch (error) {
      logError({
        label: 'UvInsertsContextProvider',
        error,
        message: 'Failed to resolve UV inserts bootstrap payload.',
        extraData: {},
      });
      setResolved(emptyResolvedUvInserts);
    }
  }, []);

  const resetUvInsertFlowState = useCallback(() => {
    setActiveConfig(null);
    setActiveTabIndex(0);
    setThemeIndex(0);
    setTextByKey({});
    setImageByKey({});
    setImageTransformByKey({});
    setThemeChooserOpen(false);
    setSuggestionChooserOpen(false);
    setSelectedSuggestionGroupIndex(0);
  }, []);

  // Reset tab and chooser state when active config changes.
  useEffect(() => {
    setActiveTabIndex(0);
    setThemeIndex(0);
    setThemeChooserOpen(false);
    setSuggestionChooserOpen(false);
    setSelectedSuggestionGroupIndex(0);
    setImageByKey({});
    setImageTransformByKey({});
    setTextByKey({});
  }, [activeConfig?.id]);

  // Reset suggestion group index when tab changes.
  useEffect(() => {
    setSelectedSuggestionGroupIndex(0);
  }, [activeTabIndex]);

  // Seed text keys from config faceElements when active config changes.
  useEffect(() => {
    if (!activeConfig) return;
    setTextByKey((prev) => {
      const next = { ...prev };
      for (const face of activeConfig.faceElements) {
        for (const el of face.elementConfig) {
          if (
            isUvInsertUserConfigurableText(el) &&
            !(el.lineItemPropertyKey in next)
          ) {
            const initial =
              el.type === 'TEXT' && el.editable === false ? (el.value ?? '') : '';
            next[el.lineItemPropertyKey] = initial;
          }
        }
      }
      return next;
    });
  }, [activeConfig?.id]);

  const setText = useCallback((key: string, value: string) => {
    setTextByKey((prev) => ({ ...prev, [key]: value }));
  }, []);

  /**
   * Builds a cart line item from the active MVVM config and current user input state.
   * Uses buildUvInsertCartLineItem from the shared view layer — no window access.
   */
  const buildCartLineItem = useCallback((options: {
    giftWrapKey: string;
    parentTitle: string;
    vendor?: string | null;
  }): UvInsertCartLineItem | null => {
    if (!activeConfig || !resolved) return null;
    return buildUvInsertCartLineItem({
      config: activeConfig,
      themeIndex,
      templateIds: resolved.templateIds,
      textByKey,
      imageByKey,
      giftWrapKey: options.giftWrapKey,
      parentTitle: options.parentTitle,
      vendor: options.vendor ?? null,
    });
  }, [activeConfig, resolved, themeIndex, textByKey, imageByKey]);

  const value = useMemo<UvInsertsContextType>(() => ({
    resolved,
    resetUvInsertFlowState,
    activeConfig,
    setActiveConfig,
    activeTabIndex,
    setActiveTabIndex,
    themeIndex,
    setThemeIndex,
    textByKey,
    setText,
    imageByKey,
    setImageByKey,
    imageTransformByKey,
    setImageTransformByKey,
    themeChooserOpen,
    setThemeChooserOpen,
    suggestionChooserOpen,
    setSuggestionChooserOpen,
    selectedSuggestionGroupIndex,
    setSelectedSuggestionGroupIndex,
    buildCartLineItem,
  }), [
    resolved,
    resetUvInsertFlowState,
    activeConfig,
    activeTabIndex,
    themeIndex,
    textByKey,
    setText,
    imageByKey,
    imageTransformByKey,
    themeChooserOpen,
    suggestionChooserOpen,
    selectedSuggestionGroupIndex,
    buildCartLineItem,
  ]);

  return (
    <UvInsertsContext.Provider value={value}>
      {children}
    </UvInsertsContext.Provider>
  );
}

/**
 * Returns the UV inserts context. Must be used inside UvInsertsContextProvider.
 * @returns {UvInsertsContextType} The UV inserts context value.
 */
export function useUvInsertsContext(): UvInsertsContextType {
  const ctx = useContext(UvInsertsContext);
  if (ctx === undefined) {
    throw new Error('useUvInsertsContext must be used within UvInsertsContextProvider');
  }
  return ctx;
}
